import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FieldDictionary } from '../../types/field';

interface FieldSelectorProps {
  value: string;
  onChange: (fieldId: string) => void;
  fields: FieldDictionary[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  loading?: boolean;
  onSearchTermChange?: (term: string) => void;
}

const FieldSelector: React.FC<FieldSelectorProps> = ({
  value,
  onChange,
  fields,
  placeholder = "Search fields...",
  disabled = false,
  required = false,
  loading = false,
  onSearchTermChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredFields, setFilteredFields] = useState<FieldDictionary[]>(fields);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Обновляем список полей при изменении пропса
  useEffect(() => {
    setFilteredFields(fields);
  }, [fields]);

  // Закрытие dropdown при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Фильтрация полей по поисковому запросу
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredFields(fields);
      return;
    }

    const filtered = fields.filter(field => {
      const searchLower = searchTerm.toLowerCase();
      return (
        field.name.toLowerCase().includes(searchLower) ||
        field.address.toLowerCase().includes(searchLower) ||
        field.state.toLowerCase().includes(searchLower) ||
        field.supportedGameTypes.some(type => type.toLowerCase().includes(searchLower))
      );
    });

    setFilteredFields(filtered);
  }, [searchTerm, fields]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchTerm(inputValue);
    onSearchTermChange?.(inputValue);
  };

  const handleFieldSelect = (fieldId: string) => {
    onChange(fieldId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleInputClick = () => {
    setIsOpen(true);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && filteredFields.length > 0) {
      handleFieldSelect(filteredFields[0].id);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  // Получаем отображаемое значение
  const getDisplayValue = (): string => {
    if (isOpen) return searchTerm;
    if (!value) return '';
    
    const selectedField = fields.find(field => field.id === value);
    return selectedField ? `${selectedField.name} - ${selectedField.address}` : '';
  };

  return (
    <Container ref={dropdownRef}>
      <InputContainer>
        <Input
          ref={inputRef}
          type="text"
          value={getDisplayValue()}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onClick={handleInputClick}
          placeholder={loading ? "Loading fields..." : placeholder}
          disabled={disabled || loading}
          required={required}
        />
        <DropdownIcon $isOpen={isOpen} onClick={() => !disabled && !loading && setIsOpen(!isOpen)}>
          {loading ? '⟳' : '▼'}
        </DropdownIcon>
      </InputContainer>
      
      {isOpen && !loading && (
        <DropdownList>
          {filteredFields.length > 0 ? (
            filteredFields.map((field) => (
              <DropdownItem
                key={field.id}
                onClick={() => handleFieldSelect(field.id)}
                $isSelected={field.id === value}
              >
                <FieldInfo>
                  <FieldName>{field.name}</FieldName>
                  <FieldAddress>{field.address}</FieldAddress>
                </FieldInfo>
                <FieldState>{field.state}</FieldState>
              </DropdownItem>
            ))
          ) : (
            <NoResults>No fields found</NoResults>
          )}
        </DropdownList>
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 30px 8px 10px;
  background: rgba(0, 0, 0, 0.8);
  border: 1px solid #66ff66;
  color: #fff;
  font-size: 12px;
  font-family: 'Courier New', monospace;
  border-radius: 0;
  box-sizing: border-box;
  cursor: pointer;

  &::placeholder {
    color: #66ff66;
    opacity: 0.7;
  }

  &:focus {
    outline: none;
    border-color: #99ff99;
    box-shadow: 0 0 5px rgba(102, 255, 102, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DropdownIcon = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  right: 10px;
  color: #66ff66;
  cursor: pointer;
  user-select: none;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.2s ease;
  font-size: 12px;
`;

const DropdownList = styled.div`
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
  right: 0 !important;
  background: rgba(0, 25, 0, 0.98) !important;
  border: 2px solid #66ff66 !important;
  border-radius: 0 !important;
  max-height: 200px !important;
  overflow-y: auto !important;
  z-index: 10050 !important;
  margin-top: 2px !important;
  box-shadow: 0 8px 16px rgba(102, 255, 102, 0.4) !important;
`;

const DropdownItem = styled.div<{ $isSelected: boolean }>`
  padding: 12px 15px !important;
  color: #ffffff !important;
  cursor: pointer !important;
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  background: ${props => props.$isSelected ? 'rgba(102, 255, 102, 0.2) !important' : 'rgba(0, 25, 0, 0.95) !important'};
  border-left: ${props => props.$isSelected ? '3px solid #66ff66 !important' : '3px solid transparent !important'};
  border-bottom: 1px solid rgba(102, 255, 102, 0.2) !important;
  font-family: 'Courier New', monospace !important;
  font-size: 11px !important;
  transition: all 0.3s ease !important;

  &:hover {
    background: rgba(102, 255, 102, 0.3) !important;
    border-left: 3px solid #66ff66 !important;
  }
  
  &:last-child {
    border-bottom: none !important;
  }
`;

const FieldInfo = styled.div`
  display: flex !important;
  flex-direction: column !important;
  flex: 1 !important;
  min-width: 0 !important;
`;

const FieldName = styled.div`
  color: #66ff66 !important;
  font-weight: bold !important;
  font-size: 12px !important;
  margin-bottom: 2px !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
`;

const FieldAddress = styled.div`
  color: #cccccc !important;
  font-size: 10px !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
`;

const FieldState = styled.div`
  color: #99ff99 !important;
  font-size: 10px !important;
  font-weight: bold !important;
  text-transform: uppercase !important;
  margin-left: 10px !important;
  white-space: nowrap !important;
`;

const NoResults = styled.div`
  padding: 12px 15px !important;
  color: #cccccc !important;
  text-align: center !important;
  font-style: italic !important;
  font-family: 'Courier New', monospace !important;
  font-size: 11px !important;
`;

export default FieldSelector;
